import React from 'react';
import QRCode from 'qrcode';

const PUBLIC_QR_DISPLAY_PX = 520;
const PUBLIC_QR_RENDER_PX = 1040;

interface ProgramContentSlidePublicQrOverlayProps {
  publicUrl: string;
  expiresAt: string;
  onClose: () => void;
  title?: string;
  hint?: string;
  /** 힌트 아래 추가 안내(소통방 입장 방법 등) */
  extra?: React.ReactNode;
  /** 간단 모드: 핵심 정보만 크게 (칩 + 짧은 주의 문구) */
  simple?: boolean;
  /** 간단 모드에서 만료 시각 칩 옆에 보여 줄 칩(접속 인원 등) */
  chips?: React.ReactNode;
}

/** 클립보드 복사 — navigator.clipboard 가 없거나(HTTP·구형 브라우저) 거부되면 textarea + execCommand 로 대체 */
async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* 아래 대체 방식으로 */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function formatExpiresLabel(expiresAt: string): string {
  if (!expiresAt.trim()) return '1시간';
  const d = new Date(expiresAt);
  if (Number.isNaN(d.getTime())) return '1시간';
  return d.toLocaleString('ko-KR', { hour: '2-digit', minute: '2-digit' });
}

const ProgramContentSlidePublicQrOverlay: React.FC<ProgramContentSlidePublicQrOverlayProps> = ({
  publicUrl,
  expiresAt,
  onClose,
  title = '공개보기 QR',
  hint = '휴대폰으로 스캔하면 보기 모드로 참여할 수 있습니다.',
  extra,
  simple = false,
  chips,
}) => {
  const [qrDataUrl, setQrDataUrl] = React.useState<string | null>(null);
  const [copyState, setCopyState] = React.useState<'idle' | 'copied' | 'failed'>('idle');
  const copyTimerRef = React.useRef<number | null>(null);
  React.useEffect(
    () => () => {
      if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
    },
    []
  );
  const handleCopyLink = React.useCallback(async () => {
    const ok = await copyTextToClipboard(publicUrl);
    setCopyState(ok ? 'copied' : 'failed');
    if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
    copyTimerRef.current = window.setTimeout(() => setCopyState('idle'), 1800);
  }, [publicUrl]);
  React.useEffect(() => setCopyState('idle'), [publicUrl]);

  React.useEffect(() => {
    let cancelled = false;
    void QRCode.toDataURL(publicUrl, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: PUBLIC_QR_RENDER_PX,
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [publicUrl]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="cp-program-slide-public-qr-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div className={`cp-program-slide-public-qr-panel${simple ? ' is-simple' : ''}`} onClick={(e) => e.stopPropagation()}>
        <p className="cp-program-slide-public-qr-title">{title}</p>
        <p className="cp-program-slide-public-qr-hint">{hint}</p>
        {extra ? <div className="cp-program-slide-public-qr-extra">{extra}</div> : null}
        <div className="cp-program-slide-public-qr-frame" aria-hidden>
          {qrDataUrl ? (
            <img
              className="cp-program-slide-public-qr-image"
              src={qrDataUrl}
              alt=""
              width={PUBLIC_QR_DISPLAY_PX}
              height={PUBLIC_QR_DISPLAY_PX}
            />
          ) : (
            <div className="cp-program-slide-public-qr-loading">QR 생성 중…</div>
          )}
        </div>
        {publicUrl ? (
          <div className="cp-program-slide-public-qr-link">
            <span className="cp-program-slide-public-qr-link-url" title={publicUrl}>
              {publicUrl}
            </span>
            <button
              type="button"
              className={`cp-program-slide-public-qr-link-copy${copyState === 'copied' ? ' is-copied' : ''}`}
              onClick={() => void handleCopyLink()}
              aria-label="링크 복사"
            >
              {copyState === 'copied' ? '복사됨' : copyState === 'failed' ? '복사 실패' : '복사'}
            </button>
            <span className="cp-program-slide-public-qr-link-status" role="status" aria-live="polite">
              {copyState === 'copied' ? '링크를 복사했어요' : copyState === 'failed' ? '복사하지 못했어요. 링크를 길게 눌러 복사해 주세요.' : ''}
            </span>
          </div>
        ) : null}
        {simple ? (
          <div className="cp-program-slide-public-qr-chips">
            {chips}
            <span className="cp-program-slide-public-qr-chip">{formatExpiresLabel(expiresAt)}까지</span>
          </div>
        ) : null}
        {simple ? (
          <p className="cp-program-slide-public-qr-caution is-short" role="note">
            학생 이름·응답이 보일 수 있으니 외부에 공유하지 마세요
          </p>
        ) : (
          <>
        <p className="cp-program-slide-public-qr-expiry">유효 시간: {formatExpiresLabel(expiresAt)}까지 (만료 후 링크 사용 불가)</p>
        <p className="cp-program-slide-public-qr-note">유효 시간이 남아 있으면 같은 QR을 다시 보여 줍니다. 만료 후 새로 열면 새 링크가 만들어집니다.</p>
        <p className="cp-program-slide-public-qr-caution" role="note">
          ⚠ 입장 후 화면에는 학급 구성원의 이름·좌석·의견·응답이 표시될 수 있습니다. 이 QR과 화면을 외부에 공유하지 않도록 주의해 주세요.
        </p>
          </>
        )}
        <button type="button" className="cp-program-slide-public-qr-close" onClick={onClose}>
          닫기
        </button>
      </div>
    </div>
  );
};

export default ProgramContentSlidePublicQrOverlay;
